/**
 * TV1-1929 (S14) — pure data-shaping + cell formatting for the Table/Pivot widget.
 *
 * The widget is fed `{ rows, columns }` straight off the Cube ResultSet (see
 * `Widgets/widgetData.ts`): `rows` from `tablePivot(pivotConfig)`, `columns`
 * from `tableColumns(pivotConfig)` — the same pivot config, so the row keys are
 * exactly the column `dataIndex`es. Everything here is pure and DOM-free so the
 * table's semantics can be tested without a renderer.
 *
 * Three jobs:
 *  1. **Flatten** the column tree. `tableColumns` returns a NESTED structure when
 *     dimensions are pivoted onto `y` (`Germany -> male -> Count`); only the LEAF
 *     carries a `dataIndex`, and only leaves key into a row. We flatten to leaves
 *     and join the ancestor `shortTitle`s into the header label, so a pivoted
 *     column reads "Germany / male / Count" instead of three identical "Count"s.
 *     (A true multi-row grouped `<thead>` with colSpans is the v2 upgrade; a
 *     single flat header row is unambiguous and correct in the meantime.)
 *  2. **Cap** the rows. See {@link capTableRows}.
 *  3. **Format** each cell from the Cube annotation — {@link formatCell}.
 */

import type { TableColumn } from '@cubejs-client/core';
import type { TableWidgetRow } from '../widgetData';

/**
 * Hard default row cap.
 *
 * `limit` exists on `WidgetDefinition` and IS emitted to Cube, but the builder
 * has no Limit section yet — so an unbounded "best sellers" query would render
 * EVERY row into the DOM. This is the backstop; it is a display cap, not a query
 * cap (the fetch already happened), and truncation is logged rather than silent.
 */
export const DEFAULT_TABLE_ROW_CAP = 100;

/** A leaf column plus the resolved header label for its (possibly pivoted) path. */
export type FlatTableColumn = TableColumn & {
  /** `shortTitle` for a flat table; `Ancestor / … / shortTitle` for a pivoted one. */
  header: string;
};

const HEADER_SEPARATOR = ' / ';

/**
 * Depth-first flatten of the `tableColumns` tree down to the leaves that
 * actually key into a `tablePivot` row, carrying the ancestor titles into
 * `header`. A column with no `children` is already a leaf.
 */
export const flattenTableColumns = (
  columns: TableColumn[] | undefined,
  ancestors: string[] = []
): FlatTableColumn[] => {
  if ( !Array.isArray( columns )) return [];

  return columns.flatMap(( column ): FlatTableColumn[] => {
    const label = column.shortTitle || column.title || column.key || '';

    if ( Array.isArray( column.children ) && column.children.length ) {
      return flattenTableColumns( column.children, [ ...ancestors, label ]);
    }

    // Defensive: a leaf without a `dataIndex` cannot address a row cell.
    if ( !column.dataIndex ) return [];

    return [{ ...column, header: [ ...ancestors, label ].filter( Boolean ).join( HEADER_SEPARATOR ) }];
  });
};

/**
 * Keep only the columns the user selected, in the CONFIGURED order — so the
 * `visibleColumns` MultiSelect doubles as a column-order control. An empty /
 * absent selection means "all columns" (not "no columns"). Unknown keys are
 * dropped rather than rendering an empty column.
 */
export const selectVisibleColumns = (
  columns: FlatTableColumn[],
  visibleColumns?: string[]
): FlatTableColumn[] => {
  if ( !Array.isArray( visibleColumns ) || !visibleColumns.length ) return columns;

  return visibleColumns
    .map(( key ) => columns.find(( column ) => column.dataIndex === key || column.key === key ))
    .filter(( column ): column is FlatTableColumn => column !== undefined );
};

/**
 * Truncate to `limit ?? DEFAULT_TABLE_ROW_CAP` rows.
 *
 * Truncation is NOT silent: a cap that bites is a signal the query needs a real
 * `limit` / a tighter filter, and a user staring at exactly 100 rows deserves to
 * be able to find out why.
 */
export const capTableRows = ( rows: TableWidgetRow[] | undefined, limit?: number ): TableWidgetRow[] => {
  if ( !Array.isArray( rows )) return [];

  const cap = typeof limit === 'number' && limit > 0 ? limit : DEFAULT_TABLE_ROW_CAP;
  if ( rows.length <= cap ) return rows;

  console.warn(
    `[table widget] ${rows.length} rows returned; rendering the first ${cap}. ` +
    'Set an explicit `limit` on the widget definition or tighten the query to avoid truncation.'
  );
  return rows.slice( 0, cap );
};

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

/**
 * Sum the numeric columns over the rows that are ACTUALLY RENDERED.
 *
 * ⚠️ Do NOT reach for `resultSet.totalRow()` here. It reads like the right API,
 * but Cube implements it as `chartPivot(pivotConfig)[0]` — it hands back the
 * FIRST pivoted row, not an aggregate. On any grouped query (i.e. every table
 * worth building) that produces a real number under a false label: "Total
 * $3,000" when $3,000 is just the top row. A confidently-wrong number is worse
 * than no number.
 *
 * The honest alternative is this: a client-side sum of what the user can see.
 * Because it sums the CAPPED rows, the caller must tell the reader when the set
 * was truncated (see `TableWidget`'s footer label) — a "Total" over the first 100
 * of 250 rows, presented as the total, would be the same lie in a new costume.
 *
 * Non-numeric columns get no total (`undefined`); a column whose values are all
 * blank stays `undefined` rather than collapsing to `0` — unknown is not zero.
 */
export const computeTotals = (
  rows: TableWidgetRow[],
  columns: FlatTableColumn[]
): TableWidgetRow | null => {
  if ( !rows.length || !columns.length ) return null;

  const totals: TableWidgetRow = {};

  for ( const column of columns ) {
    if ( !isNumericColumn( column )) continue;

    let sum = 0;
    let seen = false;

    for ( const row of rows ) {
      const value = row[column.dataIndex];
      if ( value === null || value === undefined || value === '' ) continue;

      const numeric = typeof value === 'number' ? value : Number( value );
      if ( Number.isNaN( numeric )) continue;

      sum += numeric;
      seen = true;
    }

    if ( seen ) totals[column.dataIndex] = sum;
  }

  return Object.keys( totals ).length ? totals : null;
};

// ---------------------------------------------------------------------------
// Cell formatting
// ---------------------------------------------------------------------------

/**
 * Cube annotates every column with a `type` (`'number' | 'string' | 'time' | …`,
 * or a measure type such as `'count'` / `'sum'`), and — when the cube author
 * declared them — a `format` (`'currency' | 'percent'`), an ISO-4217 `currency`
 * code, and a `granularity` for time dimensions. THAT annotation is the whole
 * reason we stopped throwing the ResultSet away.
 */
const NUMERIC_COLUMN_TYPES: ReadonlySet<string> = new Set([
  'number', 'count', 'countDistinct', 'countDistinctApprox', 'sum', 'avg', 'min', 'max', 'runningTotal'
]);

/** Numeric columns are right-aligned; everything else reads left. */
export const isNumericColumn = ( column: Pick<TableColumn, 'type'> ): boolean =>
  typeof column.type === 'number' || NUMERIC_COLUMN_TYPES.has( String( column.type ));

const isTimeColumn = ( column: Pick<TableColumn, 'type' | 'granularity'> ): boolean =>
  String( column.type ) === 'time' || Boolean( column.granularity );

/**
 * Date/time display options per Cube granularity. Coarser granularities must not
 * render a spurious day/time component — a `month` bucket is "Mar 2026", not
 * "Mar 1, 2026, 00:00".
 */
const dateOptionsFor = ( granularity?: string ): Intl.DateTimeFormatOptions => {
  switch ( granularity ) {
    case 'year':
      return { year: 'numeric' };
    case 'quarter':
    case 'month':
      return { year: 'numeric', month: 'short' };
    case 'hour':
    case 'minute':
      return { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    case 'second':
      return { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
    case 'day':
    case 'week':
    default:
      return { year: 'numeric', month: 'short', day: 'numeric' };
  }
};

/** Rendered for a null / undefined / unparseable value — NEVER `0`, never blank. */
export const EMPTY_CELL = '—';

/**
 * Format one cell from its column annotation.
 *
 * Deliberately local to this widget: the only formatter in the app
 * (`domains/accounting/utils.ts`) is hardcoded to `es-AR` / `ARS` and is
 * domain-local — reaching into it from a generic widget would be a layering
 * violation AND would mislabel every non-ARS number.
 *
 * Rules:
 * - numbers use `Intl.NumberFormat` with the browser locale;
 * - a currency symbol appears **only when the annotation supplies `currency`** —
 *   a number with no currency annotation renders as a plain number. That is
 *   accepted: guessing a currency is worse than not showing one;
 * - `format: 'percent'` appends `%` (Cube percent measures are already in
 *   percent units — multiplying by 100 here would be a 100x bug);
 * - time dimensions render per `granularity`.
 *
 * @param locale Explicit locale, for deterministic tests. Defaults to the
 *   browser/runtime locale.
 */
export const formatCell = (
  value: unknown,
  column: Pick<TableColumn, 'type' | 'format' | 'currency' | 'granularity'>,
  locale?: string
): string => {
  if ( value === null || value === undefined || value === '' ) return EMPTY_CELL;

  if ( typeof value === 'boolean' ) return String( value );

  if ( isTimeColumn( column )) {
    const date = new Date( value as string );
    if ( Number.isNaN( date.getTime())) return String( value );
    return new Intl.DateTimeFormat( locale, dateOptionsFor( column.granularity )).format( date );
  }

  // Cube returns numeric measures as STRINGS over the wire; coerce before deciding.
  const numeric = typeof value === 'number' ? value : Number( value );
  if ( isNumericColumn( column ) && !Number.isNaN( numeric )) {
    if ( column.currency ) {
      return new Intl.NumberFormat( locale, {
        style: 'currency',
        currency: column.currency
      }).format( numeric );
    }

    const formatted = new Intl.NumberFormat( locale, { maximumFractionDigits: 2 }).format( numeric );
    return column.format === 'percent' ? `${formatted}%` : formatted;
  }

  return String( value );
};
